import { Group, Button, Text, Container, Burger, Menu, Badge, Drawer, Stack, Divider, Select } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconUser, IconLogout, IconCalculator, IconBolt, IconChartLine, IconFileUpload, IconChevronDown, IconQuestionMark, IconDashboard, IconReportAnalytics, IconNews, IconMapPin, IconSun } from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../features/auth';
import { useOSD } from '../../../contexts/OSDContext';
import { useState, useEffect } from 'react';
import { getUnreadNewsCount, setLastVisit } from '../../../utils/newsTracking';
import { NEWS_ITEMS } from '../../../data/news/newsData';
import { OSD_CODES, getOSDShortName, type OSDCode } from '../../../utils/osdMetadata';
import '../../../styles/animations.css';

interface HeaderProps {
  isAuthenticated?: boolean;
  activeSection?: string | null;
}

export function Header({ isAuthenticated: isAuthenticatedProp, activeSection }: HeaderProps) {
  const [opened, { toggle }] = useDisclosure(false);
  const navigate = useNavigate();
  const { isAuthenticated, signOut } = useAuth();
  const { currentOSD, setOSD, getOSDPath } = useOSD();
  const [unreadCount, setUnreadCount] = useState(0);

  // Use auth context state if available, otherwise use prop
  const authenticated = isAuthenticated || isAuthenticatedProp;

  // Calculate unread news count on mount
  useEffect(() => {
    const count = getUnreadNewsCount(NEWS_ITEMS);
    setUnreadCount(count);
  }, []);

  // Handle news navigation - mark as visited
  const handleNewsClick = () => {
    setLastVisit();
    setUnreadCount(0);
  };

  // Handle OSD change
  const handleOSDChange = (value: string | null) => {
    if (value && OSD_CODES.includes(value as OSDCode)) {
      setOSD(value as OSDCode);
      // Note: setOSD already handles navigation to the new OSD path
    }
  };

  // OSD options for Select
  const osdOptions = OSD_CODES.map((code) => ({
    value: code,
    label: getOSDShortName(code),
  }));

  const handleLogout = async () => {
    try {
      await signOut();

      notifications.show({
        title: 'Wylogowano',
        message: 'Do zobaczenia!',
        color: 'blue',
      });

      // navigate returns a promise but we intentionally don't await it
      void navigate('/');
    } catch (error) {
      notifications.show({
        title: 'Błąd',
        message: 'Nie udało się wylogować',
        color: 'red',
      });
    }
  };

  return (
    <header style={{ borderBottom: '1px solid var(--ec-border-subtle, #dee2e6)', backgroundColor: 'var(--ec-header-bg, #ffffff)' }}>
      <Container size="xl" p="md">
        <Group justify="space-between">
          {/* Logo with OSD Selector */}
          <Group gap="sm">
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Text size="xl" fw={700} c="ecoGreen.6">
                Taryfy Prądu
              </Text>
            </Link>
            {/* OSD Selector Dropdown */}
            {currentOSD && (
              <Select
                size="xs"
                w={110}
                value={currentOSD}
                onChange={handleOSDChange}
                data={osdOptions}
                leftSection={<IconMapPin size={14} />}
                styles={{
                  input: {
                    fontWeight: 600,
                    borderColor: 'var(--mantine-color-gray-3)',
                  },
                }}
                comboboxProps={{ withinPortal: true }}
              />
            )}
          </Group>

          {/* Desktop Navigation - EXPANDED */}
          <Group gap="sm" visibleFrom="sm">
            {/* Home link */}
            <Button
              component={Link}
              to={currentOSD ? `/${currentOSD}` : '/'}
              variant={activeSection === 'home' ? 'light' : 'subtle'}
            >
              Start
            </Button>

            {/* PRIMARY CTA - Kalkulator */}
            <Button
              component={Link}
              to={currentOSD ? getOSDPath('/kalkulatory/podstawowy') : '/kalkulatory/podstawowy'}
              leftSection={<IconBolt size={18} />}
              variant={activeSection === 'kalkulatory' ? 'filled' : 'light'}
              color="ecoGreen"
            >
              Kalkulator
            </Button>

            {/* Kalkulator profesjonalny - TAURON only, next to Kalkulator */}
            {currentOSD === 'TAURON' && (
              <Button
                component={Link}
                to={getOSDPath('/kalkulatory/zaawansowany')}
                variant={activeSection === 'kalkulatory-pro' ? 'light' : 'subtle'}
                leftSection={<IconCalculator size={16} />}
              >
                Kalkulator Pro
              </Button>
            )}

            {/* Kalkulator prosumenta - TAURON only */}
            {currentOSD === 'TAURON' && (
              <Button
                component={Link}
                to={getOSDPath('/kalkulatory/prosument')}
                variant={activeSection === 'kalkulatory-prosument' ? 'light' : 'subtle'}
                leftSection={<IconSun size={16} />}
              >
                Prosument
              </Button>
            )}

            {/* Przewodnik Dropdown */}
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <Button
                  variant={activeSection === 'przewodnik' ? 'light' : 'subtle'}
                  rightSection={<IconChevronDown size={14} />}
                >
                  Przewodnik
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconBolt size={16} />}
                  component={Link}
                  to={currentOSD ? getOSDPath('/przewodnik/taryfy') : '/przewodnik/taryfy'}
                >
                  Porównanie taryf
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconChartLine size={16} />}
                  component={Link}
                  to={currentOSD ? getOSDPath('/przewodnik/wzorce') : '/przewodnik/wzorce'}
                >
                  Wzorce zużycia
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconReportAnalytics size={16} />}
                  component={Link}
                  to={currentOSD ? getOSDPath('/przewodnik/analizy') : '/przewodnik/analizy'}
                >
                  Analizy
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconQuestionMark size={16} />}
                  component={Link}
                  to={currentOSD ? getOSDPath('/przewodnik/faq') : '/przewodnik/faq'}
                >
                  FAQ
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>

            {/* Aktualności - Direct button */}
            <Button
              component={Link}
              to={currentOSD ? getOSDPath('/aktualnosci') : '/aktualnosci'}
              variant={activeSection === 'aktualnosci' ? 'light' : 'subtle'}
              leftSection={<IconNews size={16} />}
              onClick={handleNewsClick}
              rightSection={
                unreadCount > 0 && (
                  <Badge size="xs" variant="filled" color="red">
                    {unreadCount}
                  </Badge>
                )
              }
            >
              Aktualności
            </Button>

            {/* Auth Section - Available for all OSD codes */}
            {!authenticated && (
              <>
                <Button
                  variant="subtle"
                  component={Link}
                  to={currentOSD ? `/${currentOSD}/login` : '/login'}
                >
                  Zaloguj się
                </Button>
                <Button
                  variant="light"
                  component={Link}
                  to={currentOSD ? `/${currentOSD}/signup` : '/signup'}
                >
                  Załóż konto
                </Button>
              </>
            )}
            {authenticated && (
              <Menu shadow="md" width={220}>
                <Menu.Target>
                  <Button
                    variant="light"
                    leftSection={<IconUser size={16} />}
                    rightSection={<IconChevronDown size={14} />}
                  >
                    Moje konto
                  </Button>
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconDashboard size={16} />}
                    onClick={() => navigate(currentOSD ? getOSDPath('/konto/panel') : '/konto/panel')}
                  >
                    Panel klienta
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconCalculator size={16} />}
                    onClick={() => navigate(currentOSD ? getOSDPath('/konto/panel') : '/konto/panel')}
                  >
                    Moje kalkulacje
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconFileUpload size={16} />}
                    onClick={() => navigate(currentOSD ? getOSDPath('/konto/import') : '/konto/import')}
                  >
                    Import danych
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    color="red"
                    leftSection={<IconLogout size={16} />}
                    onClick={() => void handleLogout()}
                  >
                    Wyloguj się
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>

          {/* Mobile Navigation */}
          <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" aria-label={opened ? 'Zamknij menu' : 'Otwórz menu'} />
        </Group>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        opened={opened}
        onClose={toggle}
        position="right"
        title="Menu"
        padding="md"
        size="xs"
        hiddenFrom="sm"
      >
        <Stack gap="xs">
          {/* OSD Selector for mobile */}
          {currentOSD && (
            <Select
              label="Twój dystrybutor"
              size="sm"
              value={currentOSD}
              onChange={(value) => {
                handleOSDChange(value);
                toggle();
              }}
              data={osdOptions}
              leftSection={<IconMapPin size={14} />}
              mb="md"
            />
          )}

          {/* Primary CTA */}
          <Button
            fullWidth
            size="lg"
            leftSection={<IconBolt size={18} />}
            component={Link}
            to={currentOSD ? getOSDPath('/kalkulatory/podstawowy') : '/kalkulatory/podstawowy'}
            onClick={toggle}
            mb="md"
          >
            Porównaj taryfy
          </Button>

          {/* Kalkulatory rozszerzone - TAURON only */}
          {currentOSD === 'TAURON' && (
            <>
              <Divider label="Kalkulatory" labelPosition="left" />
              <Button
                fullWidth
                variant="subtle"
                justify="flex-start"
                leftSection={<IconCalculator size={16} />}
                component={Link}
                to={getOSDPath('/kalkulatory/zaawansowany')}
                onClick={toggle}
              >
                Kalkulator profesjonalny
              </Button>
              <Button
                fullWidth
                variant="subtle"
                justify="flex-start"
                leftSection={<IconSun size={16} />}
                component={Link}
                to={getOSDPath('/kalkulatory/prosument')}
                onClick={toggle}
              >
                Kalkulator prosumenta
              </Button>
            </>
          )}

          <Divider label="Przewodnik" labelPosition="left" />

          <Button
            fullWidth
            variant="subtle"
            justify="flex-start"
            leftSection={<IconBolt size={16} />}
            component={Link}
            to={currentOSD ? getOSDPath('/przewodnik/taryfy') : '/przewodnik/taryfy'}
            onClick={toggle}
          >
            Porównanie taryf
          </Button>
          <Button
            fullWidth
            variant="subtle"
            justify="flex-start"
            leftSection={<IconChartLine size={16} />}
            component={Link}
            to={currentOSD ? getOSDPath('/przewodnik/wzorce') : '/przewodnik/wzorce'}
            onClick={toggle}
          >
            Wzorce zużycia
          </Button>
          <Button
            fullWidth
            variant="subtle"
            justify="flex-start"
            leftSection={<IconReportAnalytics size={16} />}
            component={Link}
            to={currentOSD ? getOSDPath('/przewodnik/analizy') : '/przewodnik/analizy'}
            onClick={toggle}
          >
            Analizy
          </Button>
          <Button
            fullWidth
            variant="subtle"
            justify="flex-start"
            leftSection={<IconQuestionMark size={16} />}
            component={Link}
            to={currentOSD ? getOSDPath('/przewodnik/faq') : '/przewodnik/faq'}
            onClick={toggle}
          >
            FAQ
          </Button>

          <Divider my="sm" />

          {/* Aktualności */}
          <Button
            fullWidth
            variant="subtle"
            justify="flex-start"
            leftSection={<IconNews size={16} />}
            component={Link}
            to={currentOSD ? getOSDPath('/aktualnosci') : '/aktualnosci'}
            onClick={() => {
              handleNewsClick();
              toggle();
            }}
            rightSection={
              unreadCount > 0 && (
                <Badge size="xs" variant="filled" color="red">
                  {unreadCount}
                </Badge>
              )
            }
          >
            Aktualności
          </Button>

          <Divider my="sm" />

          {/* Auth Section - Available for all OSD codes */}
          {!authenticated && (
            <>
              <Button
                fullWidth
                variant="light"
                component={Link}
                to={currentOSD ? `/${currentOSD}/login` : '/login'}
                onClick={toggle}
              >
                Zaloguj się
              </Button>
              <Button
                fullWidth
                variant="filled"
                component={Link}
                to={currentOSD ? `/${currentOSD}/signup` : '/signup'}
                onClick={toggle}
              >
                Załóż konto
              </Button>
            </>
          )}
          {authenticated && (
            <>
              <Divider label="Moje konto" labelPosition="left" />
              <Button
                fullWidth
                variant="light"
                justify="flex-start"
                leftSection={<IconDashboard size={16} />}
                component={Link}
                to={currentOSD ? getOSDPath('/konto/panel') : '/konto/panel'}
                onClick={toggle}
              >
                Panel klienta
              </Button>
              <Button
                fullWidth
                variant="subtle"
                justify="flex-start"
                leftSection={<IconCalculator size={16} />}
                component={Link}
                to={currentOSD ? getOSDPath('/konto/panel') : '/konto/panel'}
                onClick={toggle}
              >
                Moje kalkulacje
              </Button>
              <Button
                fullWidth
                variant="subtle"
                justify="flex-start"
                leftSection={<IconFileUpload size={16} />}
                component={Link}
                to={currentOSD ? getOSDPath('/konto/import') : '/konto/import'}
                onClick={toggle}
              >
                Import danych
              </Button>
              <Divider my="xs" />
              <Button
                fullWidth
                variant="subtle"
                justify="flex-start"
                color="red"
                leftSection={<IconLogout size={16} />}
                onClick={() => {
                  void handleLogout();
                  toggle();
                }}
              >
                Wyloguj się
              </Button>
            </>
          )}
        </Stack>
      </Drawer>
    </header>
  );
}
